import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import { type LucideIcon, MenuIcon } from "lucide-react";
import { type MouseEventHandler, useRef, useState } from "react";
import { useClickAway, useWindowScroll } from "react-use";

import AppBarItem from "./AppBarItem";
import { useAccess } from "~/hooks";

interface Props {
	items: IAppBarItem[];
	hideScrollToTop?: boolean;
}

type AppBarItemBase = {
	label: string;
	icon: LucideIcon;
	shortLabel?: string;
};

export type IAppBarItem =
	| (AppBarItemBase & {
			onClick: MouseEventHandler<HTMLButtonElement>;
			href?: never;
	  })
	| (AppBarItemBase & { href: string; onClick?: never });

const AppBar = ({ items, hideScrollToTop }: Props) => {
	const { y } = useWindowScroll();

	const { isMember } = useAccess();

	const hasOverflow = items.length > 5;
	const [isOverflowOpen, setIsOverflowOpen] = useState(false);
	const ref = useRef(null);
	useClickAway(ref, () => {
		setIsOverflowOpen(false);
	});

	return (
		<div
			ref={ref}
			className={clsx(
				"sticky bottom-0 z-20 flex laptop:hidden justify-evenly border-t bg-white pt-0.5 dark:bg-slate-800",
				{
					"shadow-2xl shadow-black": isMember,
				},
			)}
		>
			{(hasOverflow ? items.slice(0, 4) : items).map((item) => (
				<AppBarItem item={item} key={item.label} />
			))}

			{hasOverflow && (
				<AppBarItem
					item={{
						label: "More",
						icon: MenuIcon,
						shortLabel: "More",

						onClick: () => {
							setIsOverflowOpen(!isOverflowOpen);
						},
					}}
					key="more"
				/>
			)}

			<AnimatePresence mode="wait">
				{isOverflowOpen && (
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						exit={{ opacity: 0, y: 20 }}
						id="more-app-bar-menu"
						className="absolute right-0 bottom-14 left-0 flex flex-col rounded-t-xl border-t bg-white p-2 dark:bg-slate-800"
					>
						{items.slice(4).map((item) => (
							<AppBarItem item={item} key={item.label} isOverflowed />
						))}
					</motion.div>
				)}
			</AnimatePresence>
		</div>
	);
};

export default AppBar;
